import React, { useState, useMemo } from "react";
import { Box, IconButton } from "@mui/material";
import {
  GridColDef,
  GridRowGroupingModel,
  GridRenderCellParams
} from "@mui/x-data-grid-premium";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import { useTranslate } from "src/locales";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";

interface PageHistoryProps {
  startDate: Date | null;
  endDate: Date | null;
  selectedIds: number[];
  onToggleRow: (row: any) => void;
}

export interface DietRequestHistory {
  id: number;
  diyetZamani: string;
  onayDurumu: string;
  adi: string;
  ogun: string;
  diyetTipi: string;
  not: string;
  refakatci: string;
  sonIstemTarihi: string;
  islemYapan: string;
  onayIptal: string;
}

const initialDietRequests: DietRequestHistory[] = [
  {
    id: 101,
    diyetZamani: "17.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Normal",
    ogun: "Sabah",
    diyetTipi: "Normal",
    not: "",
    refakatci: "",
    sonIstemTarihi: "17.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
  {
    id: 102,
    diyetZamani: "17.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Normal",
    ogun: "Öğlen",
    diyetTipi: "Sulu Yemek",
    not: "",
    refakatci: "",
    sonIstemTarihi: "17.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
  {
    id: 103,
    diyetZamani: "17.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Tansiyon",
    ogun: "Akşam",
    diyetTipi: "Diyet Tansiyon",
    not: "",
    refakatci: "",
    sonIstemTarihi: "17.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
  {
    id: 104,
    diyetZamani: "18.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Normal",
    ogun: "Sabah",
    diyetTipi: "Normal",
    not: "",
    refakatci: "",
    sonIstemTarihi: "18.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
  {
    id: 105,
    diyetZamani: "18.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Normal",
    ogun: "Öğlen",
    diyetTipi: "Sulu Yemek",
    not: "",
    refakatci: "",
    sonIstemTarihi: "18.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
  {
    id: 106,
    diyetZamani: "18.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Tansiyon",
    ogun: "Akşam",
    diyetTipi: "Diyet Tansiyon",
    not: "",
    refakatci: "",
    sonIstemTarihi: "18.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
  {
    id: 107,
    diyetZamani: "19.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Normal",
    ogun: "Sabah",
    diyetTipi: "Normal",
    not: "",
    refakatci: "",
    sonIstemTarihi: "19.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
  {
    id: 108,
    diyetZamani: "19.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Normal",
    ogun: "Öğlen",
    diyetTipi: "Sulu Yemek",
    not: "",
    refakatci: "",
    sonIstemTarihi: "19.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
  {
    id: 109,
    diyetZamani: "19.05.2025",
    onayDurumu: "Gönderildi",
    adi: "Tansiyon",
    ogun: "Akşam",
    diyetTipi: "Diyet Tansiyon",
    not: "",
    refakatci: "",
    sonIstemTarihi: "19.12.2024",
    islemYapan: "BYZ",
    onayIptal: "Gönderildi",
  },
];

const groupingModel: GridRowGroupingModel = ["diyetZamani"];

const PageHistory: React.FC<PageHistoryProps> = ({
  startDate,
  endDate,
  selectedIds,
  onToggleRow,
}) => {
  const { t } = useTranslate();
  const [childSelected, setChildSelected] = useState<Record<number, boolean>>(
    {}
  );
  const [dietReqs, setDietReqs] = useState<DietRequestHistory[]>(initialDietRequests);

  const filteredDietRequests = useMemo(() => {
    if (!startDate && !endDate) return dietReqs;
    const normStart = startDate
      ? new Date(startDate.setHours(0, 0, 0, 0))
      : null;
    const normEnd = endDate
      ? new Date(endDate.setHours(23, 59, 59, 999))
      : null;
    return dietReqs.filter((dr) => {
      const [d, m, y] = dr.diyetZamani.split(".");
      const dt = new Date(+y, +m - 1, +d);
      return (
        (normStart ? dt >= normStart : true) && (normEnd ? dt <= normEnd : true)
      );
    });
  }, [dietReqs, startDate, endDate, selectedIds, onToggleRow]);

  const childColumns: GridColDef[] = [
    {
      field: "selectDetail",
      headerName: "",
      width: 48,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<DietRequestHistory>) => {
        //const dr = params.row as DietRequestHistory;
        const node: any = params.rowNode;

        if (node.groupingField === "diyetZamani") {
          const groupKey = node.groupingKey as string;
          const groupRows = filteredDietRequests.filter(
            (r) => r.diyetZamani === groupKey
          );

          const allSelected = groupRows.every((r) =>
            selectedIds.includes(r.id)
          );
          const someSelected =
            !allSelected && groupRows.some((r) => selectedIds.includes(r.id));

          return (
            <ByzMuiCheckBox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => {
                const v = e.target.checked;

                setChildSelected((prev) => {
                  const next = { ...prev };
                  groupRows.forEach((r) => (next[r.id] = v));
                  return next;
                });
                if (v) {
                  groupRows
                    .filter((r) => !selectedIds.includes(r.id))
                    .forEach((r) => onToggleRow(r));
                } else {
                  groupRows
                    .filter((r) => selectedIds.includes(r.id))
                    .forEach((r) => onToggleRow(r));
                }
              }}
            />
          );
        }
      },
    },
    {
      field: "processMenu",
      headerName: "",
      width: 48,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<DietRequestHistory>) => {
        const node: any = params.rowNode;
        if (node.groupingField === "diyetZamani") {
          return (
            <ByzMuiOpenProcessMenu
              menuItems={[
                {
                  label: t("patient-diet-history.add-favorites"),
                  onClick() {},
                },
                {
                  label: t("patient-diet-history.delete-favorites"),
                  onClick() {},
                },
              ]}
              hideHeaderTitle
            />
          );
        }
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src="/assets/icons/vector/line.svg" alt="Line" />
          </Box>
        );
      },
    },
    {
      field: "detailExpand",
      headerName: "",
      width: 48,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<DietRequestHistory>) => {
        const node: any = params.rowNode;
        const api: any = params.api;
        if (node.groupingField === "diyetZamani") {
          const exp = node.childrenExpanded ?? false;
          return (
            <IconButton
              size="small"
              onClick={() => api.setRowChildrenExpansion(node.id, !exp)}
            >
              {exp ? (
                <img src="/assets/icons/vector/vector-down.svg" alt="down" />
              ) : (
                <img src="/assets/icons/vector/vector-right.svg" alt="right" />
              )}
            </IconButton>
          );
        }
        const dr = params.row as DietRequestHistory;
        const checked = !!childSelected[dr.id];
        return (
          <ByzMuiCheckBox
            checked={checked}
            onChange={(e) => {
              const v = e.target.checked;
              onToggleRow(params.row);
              setChildSelected((prev) => ({ ...prev, [dr.id]: v }));
            }}
          />
        );
      },
    },
    {
      field: "lastMenu",
      headerName: "",
      width: 48,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<DietRequestHistory>) => {
        const node: any = params.rowNode;
        if (node.groupingField !== "diyetZamani") {
          return (
            <ByzMuiOpenProcessMenu
              menuItems={[
                {
                  label: "Sık Kullanılanlara Ekle",
                  onClick() {},
                },
                {
                  label: "Sık Kullanılanlardan Çıkar",
                  onClick() {},
                },
              ]}
              hideHeaderTitle
            />
          );
        }
      },
    },
    {
      field: "diyetZamani",
      headerName: t("patient-diet-history.diet-time"),
      flex: 1,
    },
    {
      field: "onayDurumu",
      headerName: t("patient-diet-history.approval-status"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<DietRequestHistory>) => {
        const node: any = params.rowNode;
        if (node.groupingField === "diyetZamani") {
          return <span>{t("patient-diet-history.sent")}</span>;
        }
        return <span>{params.value}</span>;
      },
      cellClassName: () => {
        return "status-approved";
      },
    },
    { field: "adi", headerName: t("patient-diet-history.name"), flex: 1 },
    { field: "ogun", headerName: t("patient-diet-history.meal"), flex: 1 },
    {
      field: "diyetTipi",
      headerName: t("patient-diet-history.diet-type"),
      flex: 1,
    },
    { field: "not", headerName: t("patient-diet-history.note"), flex: 1 },
    {
      field: "refakatci",
      headerName: t("patient-diet-history.companion"),
      flex: 1,
    },
    {
      field: "sonIstemTarihi",
      headerName: t("patient-diet-history.transaction-time"),
      flex: 1,
    },
    {
      field: "islemYapan",
      headerName: t("patient-diet-history.transactor"),
      flex: 1,
    },
    {
      field: "onayIptal",
      headerName: t("patient-diet-history.approve-cancel"),
      flex: 1,
    },
  ];

  const childRows = filteredDietRequests;

  return (
    <ByzMuiDataGrid
      rows={childRows}
      columns={childColumns}
      getRowId={(r) => r.id}
      hideFooterPagination
      disableRowSelectionOnClick
      rowGroupingModel={groupingModel}
      pinnedColumns={{
        left: ["selectDetail", "processMenu", "detailExpand", "lastMenu"],
        right: [],
      }}
      columnVisibilityModel={{ diyetZamani: false }}
      groupingColDef={{
        headerName: t("patient-diet-history.diet-time"),
        width: 180,
        flex: 1,
        leafField: "diyetZamani",
        renderCell: (cellParams) => <span>{cellParams.value}</span>,
      }}
      sx={{
        "& .status-approved": { backgroundColor: "#AEADA64D" },
      }}
      customToolbar={ByzDataGridToolbar}
    />
  );
};

export default PageHistory;
